import { cn } from '@heidi/utils';
import type { RefObject } from 'react';

import { useScrollAwareTopFade } from './useScrollAwareTopFade';

/** Fade tone for chats sitting on the Work canvas — `workCanvasSurfaceClassName`,
 *  `bg-surface-container`. The name predates the canvas taking the inset
 *  container fill (DS-1850) and is kept because the agents move-plan census
 *  pins this export; it still means "the tone of the canvas behind the chat". */
export const PAGE_FADE_TONE =
  'from-surface-container to-surface-container/0 newBrandDark:from-surface-container newBrandDark:to-surface-container/0';
/** Fade tone for chats sitting on a plain `bg-surface` (card / white) area. */
export const SURFACE_FADE_TONE =
  'from-surface to-surface/0 newBrandDark:from-surface newBrandDark:to-surface/0';

interface ProjectChatScrollFadesProps {
  bottomFadeRef: RefObject<HTMLDivElement>;
  /** Kept for the ruling-31 door contract. The Work canvas is ungated
   *  `surface-container` in both themes, so the default fade no longer
   *  branches on this read. */
  insetDesign: boolean;
  /** Override the top fade's size — embedders with a header directly above
   *  the transcript (routine panes) pass a taller class (e.g. `h-16`) so text
   *  dissolves softly under it instead of clipping at a hard edge. */
  topFadeClassName?: string;
  /** Override BOTH fades' colors ({@link PAGE_FADE_TONE} /
   *  {@link SURFACE_FADE_TONE}). ProjectChat is transparent, so the host that
   *  knows the actual backdrop picks the tone; the flag-based default here
   *  assumed the chat painted its own background. */
  fadeColorClassName?: string;
  /** The transcript scroller. When supplied the top fade is hidden at rest and
   *  fades in as content scrolls beneath it, so it never covers the first
   *  message; without it the top fade is static. */
  scrollRef?: RefObject<HTMLElement>;
}

export const ProjectChatScrollFades = ({
  bottomFadeRef,
  insetDesign,
  topFadeClassName,
  fadeColorClassName,
  scrollRef,
}: ProjectChatScrollFadesProps) => {
  const topFadeRef = useScrollAwareTopFade(scrollRef);

  // Door contract: callers still pass the resolved flag. The canvas tone no
  // longer branches on it.
  void insetDesign;

  // Match the parent surface in ProjectChat so the fade dissolves into the
  // background instead of reading as a visible band. The Work canvas
  // (`workCanvasSurfaceClassName`) is `surface-container` in both themes, so one
  // tone answers both — the per-theme pairing here was tracking
  // `bg-surface-shell`'s light/dark split, which the canvas no longer has.
  const fadeColorClasses = PAGE_FADE_TONE;

  return (
    <>
      <div
        ref={topFadeRef}
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-2 bg-gradient-to-b sm:h-3',
          scrollRef &&
            'opacity-0 transition-opacity duration-200 ease-in-out motion-reduce:transition-none',
          fadeColorClasses,
          fadeColorClassName,
          topFadeClassName
        )}
        data-testid="project-chat-top-fade"
        aria-hidden="true"
      />
      <div
        ref={bottomFadeRef}
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t opacity-0',
          fadeColorClasses,
          fadeColorClassName
        )}
        data-testid="project-chat-bottom-fade"
        aria-hidden="true"
      />
    </>
  );
};
